import { Injectable, OnDestroy } from '@angular/core';
import { Compression } from 'common/utility/compression/compression';
import { Observable, Subject, combineLatest, filter, of, take, takeUntil } from 'rxjs';
import { AuthenticationService, AuthenticationState } from 'services/authentication/authentication.service';
import { IndexDbService } from 'services/indexdb/index-db.service';
import { ConnectionState } from 'services/socket/classes/connection-state';
import { NotificationStatus } from 'services/socket/classes/notification-status';
import { ResponseMessageTypes } from 'services/socket/classes/response-message-type';
import { SocketMethod } from 'services/socket/classes/socket-method';
import { SocketService } from 'services/socket/socket.service';
import { TokenService } from 'services/token/token.service';
import { Logger } from 'src/app/common/utility/logger/logger';
import { Strings } from '../../common/utility/string/string';
import {
  DashboardRequestResult,
  DashboardRequestStatus,
  IDashboardItem,
  IDashboardItemStorage,
  IDashboardList,
  IDashboardListItem
} from './';
import { DashboardStore } from './stores/dashboard';
import { DashboardSettingsStore } from './stores/dashboard-settings';

@Injectable({
  providedIn: 'root'
})
export class DashboardService implements OnDestroy {
  private unsubscribe: Subject<void> = new Subject();
  private dashboardUpdateSubject: Subject<DashboardRequestResult> = new Subject();
  private static dashboardList: Array<IDashboardList> | undefined;
  private static dashboards: Array<IDashboardItemStorage> = new Array<IDashboardItemStorage>;
  private static pendingDashboard: IDashboardItemStorage | undefined;
  private static pendingDeleteDashboardId: number | undefined;
  private static pendingTransactionId: string | undefined;
  private static dashboardsReady: boolean = false
  private unmatchedDashboards: Array<IDashboardItemStorage> = []

  constructor(
    private tokenService: TokenService,
    private socketService: SocketService,
    private authenticationService: AuthenticationService,
    private indexDbService: IndexDbService
  ) {
    this.listenForDashboardMessages();
    this.listenForAuthenticationState();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next()
    this.unsubscribe.complete();
  }

  private listenForAuthenticationState(): void {
    combineLatest([this.authenticationService.listenForAuthenticationState, this.socketService.ConnectionState]).
      pipe(takeUntil(this.unsubscribe)).subscribe(([authState, connectState]) => {
        if (authState === AuthenticationState.Authenticated && connectState === ConnectionState.Connected) {
          this.reloadDashboards();
        }
        if (authState === AuthenticationState.NotAuthenticated || connectState !== ConnectionState.Connected) {
          DashboardService.dashboardList = undefined;
        }
      });
  } // listenForAuthenticationState()

  private listenForDashboardMessages(): void {
    this.socketService.Messages.pipe(takeUntil(this.unsubscribe),
      filter((message: any) => 'messageType' in message)).
      subscribe((message: any) => {
        switch (message.messageType) {
          case ResponseMessageTypes.GetDashboardsList:
            this.processGetDashboardsList(message);
            break;
          case ResponseMessageTypes.GetDashboardById:
            this.processGetDashboardById(message);
            break;
          case ResponseMessageTypes.CreateDashboard:
            this.processCreateDashboard(message);
            break;
          case ResponseMessageTypes.DeleteDashboard:
            this.processDeleteDashboard(message);
            break;
          case ResponseMessageTypes.SaveDashboard:
            this.processSaveDashboard(message);
            break;
        } // switch()
      });
  } // listenForDashboardMessages()

  public get getDashboardList(): Array<IDashboardListItem> {
    if (DashboardService.dashboards) {
      return DashboardService.dashboards.map(item => {
        return { id: item.id, name: item.name, version: item.version }
      });
    }
    return [];
  } // get getDashboardList()

  private stringToIntArray(widgets: any): Uint8Array {
    return Uint8Array.from(widgets.replace('[', '').replace(']', '').split(','))
  } // stringToIntArray()

  public getDashboardById(id: number): IDashboardItem | undefined {
    const dash = DashboardService.dashboards.find(dashboard => dashboard.id === id)
    if (dash === undefined) {
      return undefined;
    }

    const dashboard: IDashboardItem = {
      id: dash.id,
      name: dash.name,
      version: dash.version,
      widgets: dash.widgets ? JSON.parse(<string>Compression.decompress(this.stringToIntArray(<any>dash.widgets))) : null
    }

    return dashboard;
  } // getDashboardById()

  public get isDashboardsReady(): boolean {
    return DashboardService.dashboards !== undefined;
  } // isDashboardsReady

  dashboardIndex: number = -1;
  // retry: boolean = false;
  public reloadDashboards() {
    // this.retry = false;
    const payload = {}
    DashboardService.dashboards.splice(0); // Empty the dashboards
    const request = this.socketService.createRequest(SocketMethod.GetDashboardsList, this.tokenService.AuthenticationId, payload, true);
    this.socketService.sendMessage(request)
  } // reloadDashboards()

  private processGetDashboardsList(message: any) {
    DashboardService.dashboardList = []
    if (message?.data?.status == NotificationStatus.Success) {
      DashboardService.dashboardList = JSON.parse(message.data.message);

      if (DashboardService.dashboardList?.length) {
        this.getStoredDashboards()
          .subscribe(storedDashboards => {
            this.unmatchedDashboards = this.unmatched(storedDashboards, DashboardService.dashboardList ?? [])
            if (!this.unmatchedDashboards || !this.unmatchedDashboards.length) {
              Logger.debug('Query list is up-to-date in the cache. Not fetching query settings', DashboardService.dashboardList)
              this.getStoredDashboardSettings()
            } else {
              Logger.debug(`${this.unmatchedDashboards.length} query settings are out of date or not in the cache. Adding`, this.unmatchedDashboards)
              this.storeDashboards(this.unmatchedDashboards)
                .subscribe(() => {
                  Logger.debug('Dashboards added to the cache. Adding', this.unmatchedDashboards)
                  this.requestDashboardSettings(this.unmatchedDashboards)
                })
            }

          })
      } else { // Empty list, we are done
        DashboardService.dashboards = [];
        this.dashboardsReloaded(false)
      }
    } else { // Request Failed
      Logger.error("Failed to retrieve dashboard list")
      DashboardService.dashboardList = []
      this.dashboardsReloaded(false)
    }
  } // processGetDashboardsList()

  private getServerDashboardById(id: number): void {
    // this.retry = false;
    const payload = {
      id: id
    }
    const request = this.socketService.createRequest(SocketMethod.GetDashboardById, this.tokenService.AuthenticationId, payload, true);
    this.socketService.sendMessage(request)
  } // getServerDashboardById()

  private processGetDashboardById(message: any) {
    if (message.data?.status == NotificationStatus.Success) {
      const dashboard: IDashboardItemStorage = JSON.parse(message.data.message);
      this.storeDashboardSettings(dashboard).subscribe(() => {
        Logger.debug(`Stored dashboard settings for '${dashboard.name}'`)
        this.removeUnmatchedDashboard(dashboard.id)
      })
    }
  } // processGetDashboardsList()

  private createDashboardRequestResult(status: DashboardRequestStatus, data?: any): DashboardRequestResult {
    const result: DashboardRequestResult = {
      status: status,
      data: data
    }
    return result;
  } // createDashboardRequestResult()

  public get getFirstDashboard(): IDashboardItem | undefined {
    if (DashboardService.dashboards.length) {
      const dashboard: IDashboardItem = {
        id: DashboardService.dashboards[0].id,
        name: DashboardService.dashboards[0].name,
        version: DashboardService.dashboards[0].version,
        widgets: DashboardService.dashboards[0].widgets ?
          JSON.parse(<string>Compression.decompress(this.stringToIntArray(<any>DashboardService.dashboards[0].widgets))) : null
      }
      return dashboard;
    } else {
      return undefined;
    }
  } // get firstDashboard()

  public get dashboardUpdated(): Subject<DashboardRequestResult> {
    return this.dashboardUpdateSubject;
  } // get dashboardUpdated

  public createDashboard(target: IDashboardItem): void {
    const zipped = Compression.compress(JSON.stringify(target.widgets))
    const dashboard: IDashboardItemStorage = {
      id: target.id,
      name: target.name,
      version: target.version,
      widgets: zipped !== undefined ? zipped.toString() : null
    }

    const request = this.socketService.createRequest(SocketMethod.CreateDashboard, this.tokenService.AuthenticationId, dashboard, true);
    this.socketService.sendMessage(request)
    DashboardService.pendingDashboard = dashboard;
    DashboardService.pendingTransactionId = request.transactionId ?? Strings.Empty;
  } // createDashboard()

  private processCreateDashboard(message: any): void {
    if (message.data && (message.data.status == NotificationStatus.Success) &&
      DashboardService.pendingDashboard &&
      (DashboardService.pendingTransactionId === message.transactionId) &&
      JSON.parse(message.data.message)[0]['dashboardId']) {
      DashboardService.pendingDashboard.id = JSON.parse(message.data.message)[0]['dashboardId']
      DashboardService.dashboards.push(DashboardService.pendingDashboard);
      this.dashboardUpdateSubject.next(this.createDashboardRequestResult(DashboardRequestStatus.Success, JSON.parse(message.data.message)[0]['dashboardId']));
    } else {
      this.dashboardUpdateSubject.next(this.createDashboardRequestResult(DashboardRequestStatus.Failed));
    }
    DashboardService.pendingDashboard = DashboardService.pendingTransactionId = undefined;
  } // processCreateDashboard()

  public saveDashboard(target: IDashboardItem): void {
    const zipped = Compression.compress(JSON.stringify(target.widgets));
    const dashboard: IDashboardItemStorage = {
      id: target.id,
      name: target.name,
      version: target.version,
      widgets: zipped !== undefined ? zipped.toString() : null
    }

    const request = this.socketService.createRequest(SocketMethod.SaveDashboard, this.tokenService.AuthenticationId, dashboard, true);
    this.socketService.sendMessage(request)
    DashboardService.pendingDashboard = dashboard;
    DashboardService.pendingTransactionId = request.transactionId ?? Strings.Empty;
  } // saveDashboard()

  private processSaveDashboard(message: any) {
    if (message.data && (message.data.status == NotificationStatus.Success) &&
      DashboardService.pendingDashboard &&
      (DashboardService.pendingTransactionId === message.transactionId)) {
      let ndx = DashboardService.dashboards.findIndex((dashboard) => dashboard.id == DashboardService.pendingDashboard?.id)
      DashboardService.dashboards[ndx] = DashboardService.pendingDashboard;
      DashboardService.pendingDashboard = DashboardService.pendingTransactionId = undefined;
      this.dashboardUpdateSubject.next(this.createDashboardRequestResult(DashboardRequestStatus.Success));
    } else {
      this.dashboardUpdateSubject.next(this.createDashboardRequestResult(DashboardRequestStatus.Failed));
    }
  } // processSaveDashboard()

  public deleteDashboard(target: IDashboardItem): void {
    const payload = {
      id: target.id
    }
    const request = this.socketService.createRequest(SocketMethod.DeleteDashboard, this.tokenService.AuthenticationId, payload, true);
    this.socketService.sendMessage(request)
    DashboardService.pendingDeleteDashboardId = target.id;
    DashboardService.pendingTransactionId = request.transactionId ?? Strings.Empty;
  } // deleteDashboard()

  private processDeleteDashboard(message: any) {
    if (message.data && (message.data.status == NotificationStatus.Success) &&
      DashboardService.pendingDeleteDashboardId && DashboardService.dashboards &&
      (DashboardService.pendingTransactionId === message.transactionId)) {

      const dashboardIndex = DashboardService.dashboards.findIndex(item => item.id === DashboardService.pendingDeleteDashboardId)
      DashboardService.dashboards.splice(dashboardIndex, 1);
      this.dashboardUpdateSubject.next(this.createDashboardRequestResult(DashboardRequestStatus.Success));
    } else {
      this.dashboardUpdateSubject.next(this.createDashboardRequestResult(DashboardRequestStatus.Failed));
    }
  } // processDeleteDashboard()


  private dashboardsReloaded(success: boolean = true): void {
    DashboardService.dashboardsReady = success
    this.dashboardResult(success ? DashboardRequestStatus.Reloaded : DashboardRequestStatus.Failed)
  }

  private dashboardResult(status: DashboardRequestStatus, data?: any): void {
    this.dashboardUpdateSubject.next(this.createDashboardRequestResult(status, data))
  }

  // #region IndexedDb methods
  private storeDashboardSettings(dashboard: IDashboardItemStorage): Observable<any> {
    if (!dashboard) return of(null)

    Logger.debug(`Storing Dashboard '${dashboard.name}'`)
    return this.indexDbService.add(DashboardSettingsStore, dashboard).pipe(take(1))
  }

  private storeDashboards(dashboards: any): Observable<any> {
    return this.indexDbService.addAll(DashboardStore, dashboards).pipe(take(1))
  }

  private getStoredDashboards(): Observable<any> {
    return this.indexDbService.all(DashboardStore)
      .pipe(take(1))
  }

  private unmatched(storedDashboards: Array<any>, dashboardList: Array<IDashboardList>): Array<any> {
    if (!storedDashboards) return []

    return dashboardList.filter(dashboard => !storedDashboards.some(stored =>
      stored.id === dashboard.id && stored.modified === dashboard.modified
    ))
  }

  private getStoredDashboardSettings() {
    return this.indexDbService.all(DashboardSettingsStore)
      .pipe(take(1))
      .subscribe((settings: Array<IDashboardItemStorage>) => {
        DashboardService.dashboards = settings
        Logger.debug(`Loaded ${settings.length} queries`, this.unmatchedDashboards)
        this.dashboardsReloaded()
      })

  }

  private requestDashboardSettings(queries: Array<any>): void {
    queries?.length ? queries.forEach(query => this.getServerDashboardById(query.id)) : null
  }

  private removeUnmatchedDashboard(id: number): void {
    if (!this.unmatchedDashboards?.length) return
    this.unmatchedDashboards = this.unmatchedDashboards.filter(source => source.id !== id)
    if (!this.unmatchedDashboards.length) {
      this.getStoredDashboardSettings()
    }
  }
  // #endregion

} // class DashboardService
